import React from 'react';
import { DatePicker, Flex, Switch } from 'antd';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';

import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExText } from 'jf/components/DevExText';
import { ReleaseTag } from 'jf/components/ReleaseTag';
import { useTargetingClient } from 'jf/services/useTargetingClient';

import { useStudyBuilderState } from '../StudyBuilderState';
import { useStudyUpdater } from '../useStudyUpdater';

export const ScheduleStudyStep: React.FC = () => {
  const theme = useDevExTheme();
  const state = useStudyBuilderState();
  const studyUpdater = useStudyUpdater(state.studyRef);
  const { msgProvider } = useTargetingClient();
  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // Can not select days before or on the current date, or weekends (Saturday/Sunday)
    return (
      current && (current < dayjs().endOf('day') || current.day() === 0 || current.day() === 6)
    );
  };
  return (
    <Flex vertical gap={theme.variable.spacing.lg}>
      <DevExText fontSize={'xl'} style={{ textAlign: 'center' }}>
        Schedule your survey <ReleaseTag stage="BETA" />
      </DevExText>
      {(state.slackTargetingEnabled || state.msteamsTargetingEnabled) && (
        <DevExCard title={'Survey Reminders'}>
          <Flex align="center" gap={theme.variable.spacing.sm}>
            <Switch
              value={state.enableScheduledReminders}
              size="small"
              onChange={(checked) => {
                state.update({ enableScheduledReminders: checked });
                studyUpdater.update({ enableScheduledReminders: checked });
              }}
            />
            <DevExText>Send automatic reminders to respondents who haven't responded</DevExText>
          </Flex>
          <Flex gap={theme.variable.spacing.sm}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ color: theme.color.text.secondary }} />
            <DevExText type="secondary">
              If enabled, reminders will automatically be sent every 2 days via {msgProvider} to
              respondents who have not yet responded. You can disable reminders at any point while
              the survey is open.
            </DevExText>
          </Flex>
        </DevExCard>
      )}
      <DevExCard title={'Survey Close Date'}>
        <Flex align="center" gap={theme.variable.spacing.sm}>
          <DevExText>This survey will close automatically</DevExText>
          <DatePicker
            disabledDate={disabledDate}
            value={dayjs(state.scheduledCloseDate)}
            onChange={(date) => {
              state.update({ scheduledCloseDate: date.toISOString() });
              studyUpdater.update({ scheduledCloseDate: date.toISOString() });
            }}
            allowClear={false}
          />
        </Flex>
        <Flex gap={theme.variable.spacing.sm}>
          <FontAwesomeIcon icon={faInfoCircle} style={{ color: theme.color.text.secondary }} />
          <DevExText type="secondary">
            The survey will close on the selected date at 9 AM EST. You will be notified in{' '}
            {msgProvider} 2 days before the survey closes. You can close it manually before then or
            extend the deadline while the survey is still open.
          </DevExText>
        </Flex>
      </DevExCard>
    </Flex>
  );
};
